import { useState, useEffect, useRef, useCallback } from 'react';

const BLOCKED_KEYS = {
  'F5': 'BLOCKED: F5',
  'F12': 'BLOCKED: F12',
};

const BLOCKED_MOD_COMBOS = {
  'w': 'BLOCKED: Cmd/Ctrl+W',
  'n': 'BLOCKED: Cmd/Ctrl+N',
  't': 'BLOCKED: Cmd/Ctrl+T',
  'l': 'BLOCKED: Cmd/Ctrl+L',
  'r': 'BLOCKED: Cmd/Ctrl+R',
  'u': 'BLOCKED: Cmd/Ctrl+U',
  's': 'BLOCKED: Cmd/Ctrl+S',
};

const useAntiCheat = ({ logViolation, fullscreen = true, blockCopyPaste = true } = {}) => {
  const logRef = useRef(logViolation);
  logRef.current = logViolation;
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const mod = e.ctrlKey || e.metaKey;

      if (e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        logRef.current('BLOCKED: Tab');
        return;
      }

      if (mod && BLOCKED_MOD_COMBOS[e.key.toLowerCase()]) {
        e.preventDefault();
        e.stopPropagation();
        logRef.current(BLOCKED_MOD_COMBOS[e.key.toLowerCase()]);
        return;
      }

      if (BLOCKED_KEYS[e.key]) {
        e.preventDefault();
        e.stopPropagation();
        logRef.current(BLOCKED_KEYS[e.key]);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      e.stopPropagation();
      logRef.current('BLOCKED: Right-click');
    };

    document.addEventListener('contextmenu', handleContextMenu, true);
    return () => document.removeEventListener('contextmenu', handleContextMenu, true);
  }, []);

  useEffect(() => {
    if (!blockCopyPaste) return;

    const handleClipboard = (e) => {
      e.preventDefault();
      e.stopPropagation();
      logRef.current(`BLOCKED: ${e.type}`);
    };

    document.addEventListener('copy', handleClipboard, true);
    document.addEventListener('cut', handleClipboard, true);
    document.addEventListener('paste', handleClipboard, true);

    return () => {
      document.removeEventListener('copy', handleClipboard, true);
      document.removeEventListener('cut', handleClipboard, true);
      document.removeEventListener('paste', handleClipboard, true);
    };
  }, [blockCopyPaste]);

  useEffect(() => {
    const handleBlur = () => logRef.current('WINDOW_BLUR');
    window.addEventListener('blur', handleBlur);
    return () => window.removeEventListener('blur', handleBlur);
  }, []);

  const requestFullscreen = useCallback(async () => {
    if (!fullscreen) return;
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      else if (el.msRequestFullscreen) await el.msRequestFullscreen();
      setIsFullscreen(true);
    } catch (err) {
      console.error('[Fullscreen]', err);
    }
  }, [fullscreen]);

  useEffect(() => {
    if (!fullscreen) return;
    requestFullscreen();
    const handleChange = () => {
      const ok = !!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
      if (!ok) {
        logRef.current('FULLSCREEN_EXIT');
        requestFullscreen();
      }
      setIsFullscreen(ok);
    };
    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange);
    };
  }, [fullscreen, requestFullscreen]);

  useEffect(() => {
    const s = document.createElement('style');
    s.textContent = '.exam-content,.exam-content *{user-select:none!important;-webkit-user-select:none!important;-moz-user-select:none!important;-ms-user-select:none!important}';
    document.head.appendChild(s);
    return () => s.remove();
  }, []);

  return { isFullscreen, requestFullscreen };
};

export default useAntiCheat;
